import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./en";

// English is the default everywhere (ADR-018); Polish is a user choice.
export const languages = ["en", "pl"] as const;
export type Language = (typeof languages)[number];

export function isLanguage(value: unknown): value is Language {
  return typeof value === "string" && (languages as readonly string[]).includes(value);
}

declare module "i18next" {
  interface CustomTypeOptions {
    defaultNS: "translation";
    resources: { translation: typeof en };
  }
}

const initial = document.documentElement.lang;

// English is bundled; Polish is its own chunk, loaded when it is the start
// language (index.html sets <html lang> before the first paint, and the
// import starts as this module runs) or when the user switches to it. Keeps
// the initial bundle inside the 250 KB budget.
void i18n.use(initReactI18next).init({
  resources: { en: { translation: en } },
  lng: "en",
  fallbackLng: "en",
  partialBundledLanguages: true,
  interpolation: { escapeValue: false },
  returnNull: false,
});

const loaded = new Set<Language>(["en"]);

/** Loads a language's texts once, without switching to it (e.g. search in both languages). */
export async function loadTexts(lng: Language): Promise<void> {
  if (loaded.has(lng)) return;
  const mod = await import("./pl");
  i18n.addResourceBundle(lng, "translation", mod.default, true, true);
  loaded.add(lng);
}

/** Loads a language's texts (once) and switches to it. */
export async function loadLanguage(lng: Language): Promise<void> {
  await loadTexts(lng);
  await i18n.changeLanguage(lng);
}

/** Resolves once the start language is ready; the app renders after it. */
export const i18nReady: Promise<void> = isLanguage(initial) && initial !== "en" ? loadLanguage(initial).catch(() => undefined) : Promise.resolve();

export default i18n;
